"use client";

import { Button, Image, Modal, Price, Text } from "@pamperedchef/design-system";
import type { CmsImage } from "@/composites/landing/types";
import { CheckList } from "@/composites/landing/CheckList";
import * as styles from "./KitDetailsModal.module.scss";

export interface KitProductView {
	title: string;
	sku?: string;
	image?: CmsImage;
	short_description?: string;
	price_text?: string;
	products_in_kit?: string[];
	also_includes?: string[];
}

/**
 * Best-effort parse of fixture-style price strings (e.g. `"$200.00"`) into a
 * numeric value for the DS `Price` component. Returns `undefined` when the
 * string can't be coerced — caller renders nothing in that case.
 */
function parsePriceText(text: string | undefined): number | undefined {
	if (!text) return undefined;
	const cleaned = text.replace(/[^0-9.\-]/g, "");
	if (!cleaned) return undefined;
	const value = Number.parseFloat(cleaned);
	return Number.isFinite(value) ? value : undefined;
}

export interface KitDetailsModalProps {
	kit: KitProductView;
	open: boolean;
	onClose: () => void;
	/** CTA shown at the bottom of the modal. */
	selectHref?: string;
	selectLabel?: string;
}

export function KitDetailsModal({
	kit,
	open,
	onClose,
	selectHref = "#",
	selectLabel = "Select this Kit"
}: KitDetailsModalProps) {
	return (
		<Modal
			className={styles["kit-details"]}
			size="xl"
			title={kit.title}
			isOpen={open}
			closeButtonLabel="Close kit details"
			closeButtonClassName={styles["kit-details__close"]}
			setOpen={(value) => {
				const resolved =
					typeof value === "function" ? value(open) : value;
				if (!resolved) onClose();
			}}
			splitContent={
				kit.image ? (
					<div className={styles["kit-details__media"]}>
						<Image
							src={kit.image.url}
							alt={kit.image.alt ?? kit.title}
							className={styles["kit-details__image"]}
							objectFit="contain"
						/>
					</div>
				) : undefined
			}
			primaryButton={
				<Button
					onPress={() => {
						window.location.href = selectHref;
					}}
				>
					{selectLabel}
				</Button>
			}
		>
			<div className={styles["kit-details__body"]}>
				{kit.price_text ? (
					<Price
						className={styles["kit-details__price"]}
						display={parsePriceText(kit.price_text)}
						size="large"
					/>
				) : null}

				{kit.short_description ? (
					<Text
						tag="p"
						styledAs="mdr"
						className={styles["kit-details__description"]}
					>
						{kit.short_description}
					</Text>
				) : null}

				<hr className={styles["kit-details__divider"]} />

				{kit.products_in_kit && kit.products_in_kit.length > 0 ? (
					<CheckList
						heading="Products in this kit"
						items={kit.products_in_kit}
					/>
				) : null}

				{kit.also_includes && kit.also_includes.length > 0 ? (
					<CheckList
						heading="Also include"
						items={kit.also_includes}
					/>
				) : null}
			</div>
		</Modal>
	);
}
